/**
 * `useCycle` — TanStack Query hook for a single cycle's metadata.
 *
 * Issue 011 fix. The `CycleRoutes.tsx` route wrappers
 * (`CycleDashboardRoute`, `CyclePageRoute`, `BlocksEditorRoute`)
 * register breadcrumbs at wrapper-level — they don't have the cycle
 * in scope, so before this hook landed they could only render the
 * `Cycle <id-prefix>` placeholder. This hook gives the wrappers a
 * cheap fetch they can pipe into `cycleBreadcrumbLabel(...)` so the
 * breadcrumb reflects the user-given name.
 *
 * Cache-key shape mirrors the inline `useQuery` already living inside
 * `<CyclePage/>` (`["cycle", cycleId, accountId]`) — both consumers
 * share the cache so the route wrapper's fetch warms the page-body's
 * fetch (and vice-versa).
 *
 * `staleTime: 60_000` — cycle metadata (name + horizon) doesn't churn
 * intra-session; a one-minute cache lines up with `useWorkspaces` and
 * keeps the breadcrumb stable as the lead navigates between tabs.
 *
 * `enabled: Boolean(cycleId && accountId)` — no fetch fires for the
 * logged-out path or when the URL is missing the cycle param.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { apiFetch } from "./client";

/**
 * #1334 C1b s2 — compact, member-safe partial-release summary. Present
 * on the single-cycle GET only when the team runs `sequenced_release`
 * AND a live TRUE-PARTIAL release region exists (explicit stations /
 * on-call keys or a date-bounded window — only writable from s3's
 * scoped releases). The two presets and every pre-ledger cycle leave
 * it null/absent, so today's payloads are unchanged and the
 * region-aware "pending" chrome activates exactly when a partial
 * region exists.
 */
export interface CycleReleaseSummaryRegion {
  stations: string[];
  on_call_types: string[];
  // Inclusive window; null = the cycle bound on that side.
  date_start?: string | null;
  date_end?: string | null;
}

export interface CycleReleaseSummary {
  partial: boolean;
  released_stations: string[];
  released_on_call_types: string[];
  // Inclusive union window across the partial regions; null = the
  // cycle bound on that side. KEPT for compatibility — renderers
  // prefer `regions`, which does not cross-product stations x window
  // (#1334 C1b s3, referee finding).
  date_start?: string | null;
  date_end?: string | null;
  // #1334 C1b s3 — the live true-partial regions one-by-one. Optional
  // for back-compat with pre-s3 cached payloads (treated as absent →
  // the union fallback renders).
  regions?: CycleReleaseSummaryRegion[];
}

/**
 * Minimal cycle row exposed by this hook. The full
 * `components["schemas"]["CycleResponse"]` shape carries 20+ fields
 * the breadcrumb consumers don't need — narrowing here keeps the type
 * surface small + aligns with the `CycleBreadcrumbInput` shape.
 */
export interface CycleSummary {
  id: string;
  team_id: string;
  name: string;
  state: string;
  horizon_start: string;
  horizon_days: number;
  // Issue #322: surfaced post-2026-05-12 so the SolveTab can render
  // <CyclePhasePipeline/> without round-tripping the
  // phase-promotion-impact endpoint. Optional for back-compat with any
  // pre-CycleResponse-extension snapshot in the cache during rollout.
  phase?: "draft" | "provisional" | "firm" | "immutable" | "archived";
  // Issue #330 + #331: completeness axis for Feature D / §9.11.
  // Optional for back-compat with pre-#330 cached payloads — the SPA
  // treats `undefined` as "single-axis legacy render" and the
  // <CyclePhasePipeline/> falls back to its pre-#331 visibility-only
  // behaviour.
  completeness_phase?: "nothing_published" | "on_call_published" | "fully_published";
  // #1334 C1b s2 — see `CycleReleaseSummary`. Optional: absent/null on
  // every cycle until a scoped (partial) release exists.
  release_summary?: CycleReleaseSummary | null;
}

const STALE_TIME_MS = 60_000;

export function useCycle(cycleId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: ["cycle", cycleId ?? "", accountId ?? ""],
    enabled: Boolean(cycleId && accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<CycleSummary>(`/api/cycles/${cycleId}`),
  });
}
